import { forwardRef } from "react";

import { Theme, useTheme } from "@mui/material";

import { InvalidThemeErrorMessage } from "../InvalidThemeErrorMessage";

import { mergeSx } from "@/common/helpers/mergeSx";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { DashboardTypography, DashboardTypographyProps } from "@/dashboard-design-system/Typography";

export type FormTypographyProps = Omit<DashboardTypographyProps, "vibe"> & {
  color?: string;
  vibe?: "intense" | "moderate" | "quiet" | "brand" | "supershy" | "reverse" | "error";
};

export const FormTypography = forwardRef<HTMLParagraphElement, FormTypographyProps>(
  ({ vibe, sx, children, color, ...rest }, ref) => {
    const theme = useTheme();

    if (!theme.palette.forms) {
      return <InvalidThemeErrorMessage />;
    }

    const getColor = (theme: Theme) => {
      switch (vibe) {
        case "intense":
          return theme.palette.forms.text.intense;
        case "moderate":
          return theme.palette.forms.text.moderate;
        case "quiet":
          return theme.palette.forms.text.quiet;
        case "brand":
          return theme.palette.forms.text.brand;
        case "supershy":
          return theme.palette.forms.text.supershy;
        case "reverse":
          return theme.palette.forms.text.reverse;
        case "error":
          return theme.palette.forms.text.error;
        default:
          return "inherit";
      }
    };

    const styles = mergeSx(
      {
        color: color ?? getColor(theme),
      },
      sx
    );

    return (
      <DashboardTypography sx={styles} {...rest} ref={ref}>
        {children}
      </DashboardTypography>
    );
  }
);

FormTypography.displayName = "FormTypography";
